<template>
  <div class="stage">
    <div class="blobs" aria-hidden="true">
      <span class="blob b1"></span>
      <span class="blob b2"></span>
      <span class="blob b3"></span>
      <span class="blob b4"></span>
    </div>

    <div class="stage-inner">
      <p class="kicker">Rainbow Manager</p>
      <h1>{{ t('resetPassword') }}</h1>
      <p class="lead">{{ t('forgotLead') }}</p>

      <div class="card">
        <VForm :validation-schema="formGroup" @submit="onSubmit">
          <section class="login-inputs">
            <Field v-slot="{ field, meta, errorMessage }" name="email">
              <div class="input-wrapper">
                <app-input
                  v-bind="field"
                  v-model="email"
                  placeholder="example@gmail.com"
                  label="email"
                  :icon="['fas', 'envelope']"
                  :error="meta.touched ? errorMessage : ''"
                  type="email"
                ></app-input>
                <div v-if="meta.touched && errorMessage" class="error-message">
                  {{ errorValue(errorMessage) }}
                </div>
              </div>
            </Field>
          </section>
          <div class="actions-btn">
            <app-button :loading="loading" type="submit" title="send"></app-button>
          </div>
        </VForm>

        <section class="links">
          <div class="text-center">
            <router-link :to="{ name: 'signIn' }">
              <font-awesome-icon size="sm" :icon="['fas', 'arrow-left']" />
              {{ t('goBack') }}
            </router-link>
          </div>
        </section>
      </div>
    </div>
  </div>
</template>
<script lang="ts">
import { computed, defineComponent, ref } from 'vue';
import AppButton from '@/shared/components/AppButton.vue';
import AppInput from '@/shared/components/AppInput.vue';
import * as yup from 'yup';
import { Field, Form as VForm } from 'vee-validate';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import { useErrorMessage } from '@/composables/useError';

export default defineComponent({
  components: { Field, VForm, AppInput, AppButton },

  setup() {
    const store = useStore();
    const { t } = useI18n();
    const email = ref('');
    const { errorValue } = useErrorMessage();
    const loading = computed(() => store.getters['authFirebase/isLoading']);
    const formGroup = yup.object({
      email: yup.string().required('validation.required').email('validation.email'),
    });

    const onSubmit = async (form: { email: string }) => {
      try {
        await store.dispatch('authFirebase/resetPassword', form.email);
      } catch (e) {
        console.error(e);
      }
    };

    return {
      formGroup,
      onSubmit,
      email,
      loading,
      t,
      errorValue,
    };
  },
});
</script>

<style scoped lang="scss">
.stage {
  position: relative;
  isolation: isolate;
  min-height: 100%;
  display: flex;
  align-items: safe center;
  justify-content: center;
  padding: 32px 28px 64px;
  color: #f6f3ee;
  background: #141018;
}

.blobs {
  position: absolute;
  inset: 0;
  z-index: 0;
  overflow: hidden;
  pointer-events: none;
}

.blob {
  position: absolute;
  border-radius: 50%;
  filter: blur(70px);
  opacity: 0.55;
  animation: blob-drift 22s ease-in-out infinite;
}

.b1 {
  width: 48vw;
  height: 48vw;
  left: -8%;
  top: -18%;
  background: #fa496e;
  animation-duration: 18s;
}

.b2 {
  width: 42vw;
  height: 42vw;
  right: -10%;
  top: 8%;
  background: #a67eff;
  animation-duration: 24s;
  animation-direction: reverse;
}

.b3 {
  width: 38vw;
  height: 38vw;
  left: 18%;
  bottom: -16%;
  background: #52dbff;
  animation-duration: 20s;
}

.b4 {
  width: 28vw;
  height: 28vw;
  right: 18%;
  bottom: 8%;
  background: #a67eff;
  opacity: 0.7;
  animation-duration: 16s;
  animation-direction: reverse;
}

.stage-inner {
  position: relative;
  z-index: 1;
  width: min(460px, 100%);
  text-align: center;
}

.kicker {
  margin: 0 0 16px;
  font-size: 11px;
  letter-spacing: 0.28em;
  text-transform: uppercase;
  color: rgba(246, 243, 238, 0.7);
}

h1 {
  margin: 0 auto 12px;
  font-size: clamp(2.2rem, 6vw, 3.4rem);
  font-weight: 600;
  line-height: 1.05;
  letter-spacing: -0.04em;
}

.lead {
  margin: 0 auto 28px;
  max-width: 36ch;
  font-size: 1.05rem;
  line-height: 1.55;
  color: rgba(246, 243, 238, 0.78);
}

.card {
  padding: 28px 24px 24px;
  border: 1px solid rgba(246, 243, 238, 0.16);
  border-radius: 20px;
  background: rgba(20, 16, 24, 0.28);
  text-align: left;
}

.login-inputs {
  display: flex;
  flex-direction: column;
  gap: 18px;
  margin-bottom: 22px;
}

.input-wrapper {
  position: relative;
}

.error-message {
  position: absolute;
  right: 16px;
  color: #fa496e;
  font-size: 12px;
  font-weight: 600;
}

.actions-btn {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.links {
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-top: 22px;
}

.text-center {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  font-size: 13px;
  line-height: 1.5;
  color: rgba(246, 243, 238, 0.62);
}

.text-center a {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  color: #f6f3ee;
  text-decoration: none;
}

.text-center a:hover {
  color: #fff;
}

:deep(.label) {
  padding: 0 8px 8px;
  font-size: 13px;
  font-weight: 600;
  letter-spacing: 0.02em;
  color: rgba(246, 243, 238, 0.78);
}

:deep(.input100) {
  color: #f6f3ee;
  background: rgba(20, 16, 24, 0.48);
  border: 1px solid rgba(246, 243, 238, 0.16);
  border-radius: 999px;
}

:deep(.input100::placeholder) {
  color: rgba(246, 243, 238, 0.38);
  text-transform: none;
}

:deep(.input100:focus::placeholder) {
  color: transparent;
}

:deep(.symbol-input100) {
  color: rgba(246, 243, 238, 0.55);
}

:deep(.input100:focus ~ .symbol-input100) {
  color: #f6f3ee;
}

:deep(.focus-input100) {
  display: none;
}

:deep(.error) {
  border-color: #fa496e;
}

:deep(.btn) {
  height: 46px;
  border-radius: 999px;
  font-size: 13px;
  font-weight: 600;
  letter-spacing: 0.04em;
}

:deep(.btn.success) {
  color: #141018;
  background: #f6f3ee;
}

:deep(.btn:not(.raised, .gradient):hover) {
  color: #141018;
  background: #fff;
}

:deep(.btn.success .loader) {
  border-color: rgba(20, 16, 24, 0.2);
  border-top-color: #141018;
}

:deep(.btn:disabled) {
  color: rgba(20, 16, 24, 0.45);
  background: rgba(246, 243, 238, 0.35);
}

@keyframes blob-drift {
  0%,
  100% {
    transform: translate(0, 0) scale(1);
  }

  50% {
    transform: translate(5%, 7%) scale(1.08);
  }
}

@media (max-width: 600px) {
  .stage {
    padding: 20px 18px 40px;
    align-items: flex-start;
  }

  .card {
    padding: 22px 18px 20px;
  }
}

@media (prefers-reduced-motion: reduce) {
  .blob {
    animation: none;
  }
}
</style>
